import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  API_ROUTE_DEFINITIONS,
  CATEGORIES,
  SOURCE_ENVIRONMENT,
  type CatalogResponse,
  type DomainData,
  type Envelope,
  type RouteDefinition,
  type Status,
} from "@contracts/index";
import { deriveInsights, type MonitoringInsight } from "./insights";

interface OverviewData {
  sourceEnvironment: string;
  cards: ReadonlyArray<Readonly<Record<string, unknown>>>;
  health: { status: Status; label: string };
  asOf: string | null;
}

interface HealthData {
  checks: ReadonlyArray<{ key: string; label: string; status: string }>;
  sourceEnvironment: string;
}

type UnknownEnvelope = Envelope<DomainData | CatalogResponse | OverviewData | HealthData | Readonly<Record<string, unknown>>>;

const isRecord = (value: unknown): value is Readonly<Record<string, unknown>> => typeof value === "object" && value !== null && !Array.isArray(value);

const statusLabel = (status: string): string => status.replaceAll("_", " ");

const statusTone = (status: string): string => {
  if (status === "HEALTHY" || status === "CONFIGURED" || status === "ENFORCED") return "healthy";
  if (status === "DEGRADED" || status === "STALE" || status === "PARTIAL") return "warning";
  if (status === "CRITICAL") return "critical";
  return "neutral";
};

const formatAsOf = (value: string | null | undefined): string => {
  if (!value) return "Awaiting source";
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "Unknown";
};

const formatMetricValue = (value: number | null): string => value === null ? "—" : new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);

class ApiRequestError extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
    this.name = "ApiRequestError";
  }
}

async function getJson(path: string, signal: AbortSignal): Promise<UnknownEnvelope> {
  const response = await fetch(path, { signal, headers: { accept: "application/json" } });
  const body = await response.json() as unknown;
  if (!response.ok) {
    const message = isRecord(body) && isRecord(body.error) && typeof body.error.message === "string" ? body.error.message : `Request failed (${response.status})`;
    const code = isRecord(body) && isRecord(body.error) && typeof body.error.code === "string" ? body.error.code : "REQUEST_FAILED";
    throw new ApiRequestError(code, message);
  }
  if (!isRecord(body) || body.schemaVersion !== "1.0" || !isRecord(body.meta)) throw new Error("The monitoring API returned an invalid envelope");
  return body as unknown as UnknownEnvelope;
}

function StatusBadge({ status }: { status: string }): ReactElement {
  return <span className={`status-badge ${statusTone(status)}`}><span className="status-dot" aria-hidden="true" />{statusLabel(status)}</span>;
}

function EmptyState({ status, message, sourceKey }: { status: string; message: string; sourceKey?: string }): ReactElement {
  return (
    <section className="empty-state" aria-live="polite">
      <div className="empty-icon" aria-hidden="true">⌁</div>
      <div>
        <StatusBadge status={status} />
        <h3>Waiting for a verified source</h3>
        <p>{message}</p>
        {sourceKey ? <p className="source-key">Source key · {sourceKey}</p> : null}
      </div>
    </section>
  );
}

function MiniChart({ values }: { values: ReadonlyArray<number> }): ReactElement {
  if (!values.length) return <div className="chart-empty">No time series has been promoted yet.</div>;
  if (values.length === 1) {
    return <div className="chart chart-single" role="img" aria-label="Single promoted metric observation"><div className="single-observation"><strong>{formatMetricValue(values[0] ?? null)}</strong><span>Single promoted observation · the next source run establishes movement</span></div><div className="chart-axis"><span>Current snapshot</span><span>Next snapshot pending</span></div></div>;
  }
  const max = Math.max(...values, 1);
  const points = values.map((value, index) => `${(index / Math.max(values.length - 1, 1)) * 100},${100 - (value / max) * 86 - 7}`).join(" ");
  return (
    <div className="chart" role="img" aria-label="Promoted metric trend">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none">
        <path className="chart-area" d={`M 0,100 L ${points.replaceAll(" ", " L ")} L 100,100 Z`} />
        <polyline className="chart-line" points={points} />
      </svg>
      <div className="chart-axis"><span>Earlier</span><span>Latest</span></div>
    </div>
  );
}

function InsightStat({ label, value, detail, tone = "default" }: { label: string; value: string; detail: string; tone?: "default" | "accent" }): ReactElement {
  return <article className={`insight-stat ${tone}`}><span className="card-kicker">{label}</span><strong>{value}</strong><span>{detail}</span></article>;
}

function InsightRow({ label, children }: { label: string; children: ReactElement | string }): ReactElement {
  return <div className="insight-row"><span className="insight-label">{label}</span><div className="insight-text">{children}</div></div>;
}

function InsightSummary({ route, insight }: { route: RouteDefinition; insight: MonitoringInsight }): ReactElement {
  const deltaLabel = insight.delta === null ? "No prior comparison" : `${insight.delta > 0 ? "+" : ""}${formatMetricValue(insight.delta)} vs prior`;
  return (
    <>
      <section className="insight-strip" aria-label="Monitoring signal summary">
        <InsightStat label="Latest signal" value={formatMetricValue(insight.latestValue)} detail={`${route.unit} · ${insight.observationLabel}`} tone="accent" />
        <InsightStat label="Movement" value={deltaLabel} detail={insight.whatChanged} />
        <InsightStat label="Coverage" value={insight.coverageLabel} detail="Expected source coverage" />
        <InsightStat label="Freshness" value={insight.freshnessLabel} detail="Last source heartbeat" />
      </section>
      <section className="insight-grid" aria-label="Monitoring insights">
        <article className="panel decision-panel">
          <div className="panel-heading"><div><div className="eyebrow">Decision lens</div><h2>{insight.question}</h2></div><StatusBadge status={insight.status} /></div>
          <div className="insight-list">
            <InsightRow label="Watch for">{insight.watchFor}</InsightRow>
            <InsightRow label="What changed">{insight.whatChanged}</InsightRow>
            <InsightRow label="Next action">{insight.nextAction}</InsightRow>
          </div>
        </article>
        <article className="panel context-panel">
          <div className="panel-heading"><div><div className="eyebrow">Observed context</div><h2>Signals in this snapshot</h2></div><span className="cadence-icon" aria-hidden="true">⌁</span></div>
          <p className="panel-intro">These labels come directly from the promoted rows. They are context, not inferred targets.</p>
          <div className="insight-context">
            {insight.observedDimensions.length ? insight.observedDimensions.map((dimension) => <span className="insight-tag" key={dimension}>{dimension}</span>) : <span className="insight-muted">No dimensional values were promoted.</span>}
          </div>
          <div className="context-footnote">Source status · {statusLabel(insight.status)} · {insight.observationLabel}</div>
        </article>
      </section>
    </>
  );
}

function Overview({ data, health, onOpenHealth, error, accessBlocked }: { data: OverviewData | null; health: HealthData | null; onOpenHealth: () => void; error: string | null; accessBlocked: boolean }): ReactElement {
  const overall = data?.health.status ?? "NOT_CONFIGURED";
  const cardByDomain = new Map((data?.cards ?? []).map((card) => [typeof card.domain === "string" ? card.domain : "", card]));
  return (
    <>
      {error && accessBlocked ? <div className="error-banner" role="status"><strong>Cloudflare Access protects live status.</strong><span>Sign in through the approved Access policy to view production snapshots, or run <code>npm run dev:local</code> for the isolated local fixture view.</span></div> : error ? <div className="error-banner" role="alert"><strong>Source access is not ready.</strong><span>{error}</span></div> : null}
      <section className="hero-grid">
        <div className="hero-copy">
          <div className="eyebrow">Customer portfolio insights</div>
          <h1>Monitor what is changing across SLiCK.</h1>
          <p className="hero-subtitle">A source-backed view of adoption, accounts, support, renewals and connected experiences.</p>
          <div className="hero-actions"><button className="button primary" onClick={onOpenHealth}>Review source health <span aria-hidden="true">→</span></button><span className="last-refreshed">Auto-refresh · 5m health / 15m facts</span></div>
        </div>
        <div className="hero-status-card">
          <div className="card-kicker">Portfolio posture</div>
          <StatusBadge status={overall} />
          <strong>{data?.health.label ?? "Relay not configured"}</strong>
          <span>Last promoted fact · {formatAsOf(data?.asOf)}</span>
        </div>
      </section>
      <section className="section-heading"><div><div className="eyebrow">Cross-functional overview</div><h2>Signals at a glance</h2></div><span className="section-note">Values appear after quality checks pass.</span></section>
      <section className="kpi-grid">
        {[
          ["Purchased APs", "trends-purchased"],
          ["Activated APs", "trends-activation"],
          ["APs onboarded", "trends-usage-onboarded"],
          ["Open tickets", "support-open-tickets"],
          ["Renewals", "renewals"],
          ["Sensor coverage", "sensor-connect"],
        ].map(([label, domain]) => {
          const card = cardByDomain.get(domain ?? "");
          const value = card && (card.status === "HEALTHY" || card.status === "STALE") && typeof card.value === "number" ? card.value.toLocaleString() : "—";
          const footnote = card && typeof card.status === "string" ? `${statusLabel(card.status)} · ${formatAsOf(typeof card.sourceAsOf === "string" ? card.sourceAsOf : null)}` : "Awaiting verified source";
          return <article className="kpi-card" key={domain}><span className="card-kicker">{label}</span><strong className="metric-placeholder">{value}</strong><span className="kpi-footnote">{footnote}</span></article>;
        })}
      </section>
      <section className="overview-columns">
        <article className="panel readiness-panel">
          <div className="panel-heading"><div><div className="eyebrow">Runtime readiness</div><h2>Trust boundary</h2></div><StatusBadge status={overall} /></div>
          <p className="panel-intro">The dashboard is intentionally honest while the relay, D1 snapshots and Access policy are being connected.</p>
          <div className="readiness-list">
            {(health?.checks ?? [
              { key: "relay", label: "Cisco API relay", status: "NOT_CONFIGURED" },
              { key: "core", label: "Core D1 snapshot", status: "NOT_CONFIGURED" },
              { key: "history", label: "90-day history D1", status: "NOT_CONFIGURED" },
              { key: "access", label: "Cloudflare Access", status: "NOT_CONFIGURED" },
            ]).map((check) => <div className="readiness-row" key={check.key}><span>{check.label}</span><StatusBadge status={check.status} /></div>)}
          </div>
        </article>
        <article className="panel cadence-panel">
          <div className="panel-heading"><div><div className="eyebrow">Collection rhythm</div><h2>Designed for signal, not noise</h2></div><span className="cadence-icon" aria-hidden="true">◷</span></div>
          <div className="cadence-row"><span className="cadence-number">05</span><div><strong>Health checks</strong><p>Every five minutes, with freshness and incident state.</p></div></div>
          <div className="cadence-row"><span className="cadence-number">15</span><div><strong>Business facts</strong><p>Every fifteen minutes, with curated aggregates and changes.</p></div></div>
          <div className="cadence-row"><span className="cadence-number">90</span><div><strong>History window</strong><p>Days of segment trends and a change ledger on the Free plan.</p></div></div>
        </article>
      </section>
    </>
  );
}

function Detail({ route, envelope, loading, error, accessBlocked }: { route: RouteDefinition; envelope: Envelope<DomainData> | null; loading: boolean; error: string | null; accessBlocked: boolean }): ReactElement {
  const rows = envelope?.data.rows ?? [];
  const detailStatus = envelope?.meta.status ?? (loading ? "LOADING" : "NOT_CONFIGURED");
  const insight = envelope ? deriveInsights(route, envelope) : null;
  const showInsights = Boolean(insight && !loading && envelope && envelope.meta.status !== "NOT_CONFIGURED" && envelope.meta.status !== "DOCUMENTATION_GAP");
  return (
    <>
      <div className="detail-heading"><div><div className="breadcrumb">{route.category} / {route.label}</div><h1>{route.label}</h1><p>{route.dimensions.length ? `Filterable by ${route.dimensions.join(", ")}.` : "Curated monitoring view."}</p></div><div className="detail-status"><StatusBadge status={detailStatus} /><span>As of · {formatAsOf(envelope?.meta.sourceAsOf)}</span></div></div>
      {error && accessBlocked ? <EmptyState status="NOT_CONFIGURED" message="This production view is protected by Cloudflare Access. Sign in through the approved policy, or run npm run dev:local for the isolated local fixture view." sourceKey="Cloudflare Access" /> : error ? <div className="error-banner" role="alert"><strong>Could not load this view.</strong><span>{error}</span></div> : null}
      {loading ? <div className="loading-panel"><span className="spinner" aria-hidden="true" />Loading verified snapshot…</div> : null}
      {showInsights && insight ? <InsightSummary route={route} insight={insight} /> : null}
      {!loading && envelope && envelope.meta.status !== "HEALTHY" && envelope.meta.status !== "STALE" ? <EmptyState status={envelope.meta.status} message={route.documentationGap ? "The legacy Sales Motion path is not queried directly. Provide an approved HTTPS relay mapping to enable this view." : "No current snapshot is available. Configure the Cisco API relay and let the quality gate promote the next packet."} sourceKey={route.sourceKey} /> : null}
      {!loading && envelope && (envelope.meta.status === "HEALTHY" || envelope.meta.status === "STALE") ? <section className="panel chart-panel"><div className="panel-heading"><div><div className="eyebrow">{route.unit} trend</div><h2>{insight?.hasTemporalSeries ? "Promoted movement" : "Promoted snapshot"}</h2></div><span className="source-key">{envelope.meta.provenance[0]?.sourceKey ?? route.sourceKey}</span></div><MiniChart values={insight?.chartValues ?? []} /></section> : null}
      {!loading && envelope && rows.length > 0 ? <section className="panel data-table-panel"><div className="panel-heading"><div><div className="eyebrow">Exact lookup</div><h2>Current records</h2></div><span className="section-note">{rows.length} promoted records</span></div><div className="table-wrap"><table><thead><tr>{Object.keys(rows[0] ?? {}).slice(0, 6).map((key) => <th key={key}>{key}</th>)}</tr></thead><tbody>{rows.slice(0, 50).map((row, index) => <tr key={String(row.recordKey ?? row.id ?? index)}>{Object.keys(rows[0] ?? {}).slice(0, 6).map((key) => <td key={key}>{typeof row[key] === "object" ? JSON.stringify(row[key]) : String(row[key] ?? "—")}</td>)}</tr>)}</tbody></table></div></section> : null}
    </>
  );
}

export function App(): ReactElement {
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [health, setHealth] = useState<HealthData | null>(null);
  const [selectedPath, setSelectedPath] = useState("/api/v1/overview");
  const [detail, setDetail] = useState<Envelope<DomainData> | null>(null);
  const [detailPath, setDetailPath] = useState<string | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [mobileNav, setMobileNav] = useState(false);

  const selectedRoute = useMemo(() => API_ROUTE_DEFINITIONS.find((route) => route.path === selectedPath) ?? null, [selectedPath]);
  const routesByCategory = useMemo(() => CATEGORIES.map((category) => ({ category, routes: (catalog?.routes ?? API_ROUTE_DEFINITIONS).filter((route) => route.category === category.key) })), [catalog]);

  useEffect(() => {
    const controller = new AbortController();
    void Promise.all([getJson("/api/v1/catalog", controller.signal), getJson("/api/v1/overview", controller.signal), getJson("/api/v1/health", controller.signal)])
      .then(([catalogResponse, overviewResponse, healthResponse]) => {
        if (isRecord(catalogResponse.data)) setCatalog(catalogResponse.data as unknown as CatalogResponse);
        if (isRecord(overviewResponse.data)) setOverview(overviewResponse.data as unknown as OverviewData);
        if (isRecord(healthResponse.data)) setHealth(healthResponse.data as unknown as HealthData);
      })
      .catch((reason: unknown) => {
        if (reason instanceof Error && reason.name !== "AbortError") {
          setError(reason.message);
          setErrorCode(reason instanceof ApiRequestError ? reason.code : null);
        }
      })
      .finally(() => controller.abort());
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!selectedRoute) { setDetail(null); setDetailPath(null); return; }
    const controller = new AbortController();
    setLoadingDetail(true);
    setDetail(null);
    setDetailPath(null);
    setError(null);
    setErrorCode(null);
    void getJson(selectedRoute.path, controller.signal)
      .then((response) => {
        if (!controller.signal.aborted) {
          setDetail(response as Envelope<DomainData>);
          setDetailPath(selectedRoute.path);
        }
      })
      .catch((reason: unknown) => {
        if (reason instanceof Error && reason.name !== "AbortError") {
          setError(reason.message);
          setErrorCode(reason instanceof ApiRequestError ? reason.code : null);
        }
      })
      .finally(() => { if (!controller.signal.aborted) setLoadingDetail(false); });
    return () => controller.abort();
  }, [selectedRoute]);

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileNav ? "open" : ""}`}>
        <div className="brand"><div className="brand-mark" aria-hidden="true">◒</div><div><strong>CISCO</strong><span>SLiCK monitoring</span></div></div>
        <div className="source-pill"><span className="pulse" aria-hidden="true" />{SOURCE_ENVIRONMENT}<span className="lock" aria-label="Read-only">⌑</span></div>
        <nav aria-label="Monitoring sections">
          <button className={`nav-home ${selectedPath === "/api/v1/overview" ? "active" : ""}`} onClick={() => { setSelectedPath("/api/v1/overview"); setMobileNav(false); }}><span className="nav-glyph">⌂</span>Overview</button>
          {routesByCategory.map(({ category, routes }) => <div className="nav-section" key={category.key}><div className="nav-section-label">{category.label}</div>{routes.map((route) => <button className={`nav-item ${selectedPath === route.path ? "active" : ""}`} key={route.path} title={route.label} onClick={() => { setSelectedPath(route.path); setMobileNav(false); }}><span className="nav-glyph" aria-hidden="true">{category.key === "accounts" ? "•" : "—"}</span>{route.label}</button>)}</div>)}
        </nav>
        <div className="sidebar-footer"><span className="footer-indicator" />Free-plan safe mode<div>Read-only · v1.0 contract</div></div>
      </aside>
      <main className="main-content">
        <header className="topbar"><button className="menu-button" aria-label="Open navigation" onClick={() => setMobileNav((value) => !value)}>☰</button><div className="topbar-title">{selectedRoute?.label ?? "Portfolio overview"}</div><div className="topbar-actions"><label className="filter-control">Window<select defaultValue="30d" aria-label="Time window"><option value="24h">24 hours</option><option value="7d">7 days</option><option value="30d">30 days</option><option value="90d">90 days</option></select></label><label className="filter-control">Region<select defaultValue="all" aria-label="Region"><option value="all">All regions</option><option value="IO">India / IO</option><option value="EU">Europe / EU</option><option value="SG">Singapore / SG</option></select></label><button className="icon-button" aria-label="Refresh data" onClick={() => window.location.reload()}>↻</button><div className="avatar" aria-label="Read-only monitor">SM</div></div></header>
        <div className="content-wrap">{selectedRoute ? <Detail route={selectedRoute} envelope={detailPath === selectedRoute.path ? detail : null} loading={loadingDetail} error={error} accessBlocked={errorCode === "ACCESS_REQUIRED"} /> : <Overview data={overview} health={health} error={error} accessBlocked={errorCode === "ACCESS_REQUIRED"} onOpenHealth={() => { const healthRoute = API_ROUTE_DEFINITIONS.find((route) => route.domain === "trends-health"); if (healthRoute) setSelectedPath(healthRoute.path); }} />}</div>
        <footer className="main-footer"><span>SLiCK monitoring · source-backed, read-only</span><span>Environment · {SOURCE_ENVIRONMENT}</span><a href="/api/v1/openapi.json">OpenAPI</a></footer>
      </main>
    </div>
  );
}
